/* Elements */
const domParentContainer = document.querySelector('.guideline-article__container-has-toc') as HTMLDivElement;
const domTocTargetContainer = document.querySelector('.toc-target-container') as HTMLDivElement;
const domHeader = document.querySelector('header') as HTMLElement;
const domPixel = document.getElementById('toc-pixel') as HTMLDivElement;
const closeTocButton = document.querySelector('.guideline-article__toc-close-button') as HTMLButtonElement;
const anchorMenu = document.getElementById('guideline-article__toc-list') as HTMLUListElement;
const template = document.getElementById('js-anchorlink-menu-template') as HTMLTemplateElement;
const toc = document.querySelector('.guideline-article__toc') as HTMLDivElement;
const tocButton = document.querySelector('.guideline-article__toc-button') as HTMLButtonElement;
const tocContainer = document.querySelector('.guideline-article__toc-container') as HTMLDivElement;
const tocInnerWrapper = document.querySelector('.guideline-article__toc-inner-wrapper') as HTMLDivElement;

let activeLink: HTMLAnchorElement | null = null;
let tocInitialized: boolean = false;

/* Create TOC li elements */
const initTableOfContents = (): void => {
    closeTocButton.addEventListener('click', () => {
        closeToc();
    });
    
    /* Escape key listener to close the TOC */
    document.addEventListener('keyup', (event: KeyboardEvent) => {
        if (JSON.parse(tocButton.dataset.open as string) && event.key === 'Escape') {
            closeToc();
        }
    });

    tocButton?.addEventListener('click', () => {
        showToc();
    });
    createListItemsAndRender();
};

/* Listener callback */
const handleKeyup = (event: KeyboardEvent): void => {
    listenForBlur(event);
};

/* Hides toc menu on small screens */
const closeToc = (): void => {
    if (!tocButton || !toc) {
        return;
    }

    tocButton.dataset.open = 'false';
    tocButton.setAttribute('aria-expanded', 'false');
    toc.classList.remove('guideline-article__toc--open');
    toc.parentElement?.classList.remove('page-with-sidemenu__side-menu--open');
    document.body.classList.remove('overflow-hidden');

    document.removeEventListener('keyup', handleKeyup);
};

/* Shows toc menu on small screens */
const showToc = (): void => {
    if (!toc || !tocButton || !closeTocButton) {
        return;
    }

    tocButton.dataset.open = 'true';
    tocButton.setAttribute('aria-expanded', 'true');
    toc.classList.add('guideline-article__toc--open');
    toc.parentElement?.classList.add('page-with-sidemenu__side-menu--open');
    document.body.classList.add('overflow-hidden');

    setTimeout(() => {
        closeTocButton?.focus();
        document.addEventListener('keyup', handleKeyup);
    }, 10);
};

const createListItemsAndRender = (): void => {
    if (!domTocTargetContainer || !tocContainer || !template) {
        return;
    }

    const headings = domTocTargetContainer.querySelectorAll('h2');

    let offset = 0;
    if (domHeader) {
        offset = domHeader.offsetHeight;
    }

    const urlHash: string = window.location.hash;

    if (headings && headings.length) {
        headings.forEach((heading, index) => {
            heading.dataset.heading = `heading-${index}`;

            if (urlHash && `#heading-${index}` === urlHash) {
                // Page loaded with this hash, scroll to it
                const targetPosition = heading.getBoundingClientRect().top + window.scrollY - offset;
                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth',
                });
            }

            const clone = document.importNode(template.content, true);
            const link = clone.querySelector("a");

            if (link) {
                link.href = `#heading-${index}`;
                link.textContent = heading.textContent;
            }

            anchorMenu.appendChild(clone);

            link?.addEventListener('click', () => {
                closeToc();

                const targetPosition = heading.getBoundingClientRect().top + window.scrollY - offset;

                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth',
                });
                
                setTimeout(() => {
                    tocButton?.focus();
                }, 0);
            });
        });

        window.addEventListener('scroll', listenForScroll);
        window.addEventListener('beforeunload', () => {
            window.removeEventListener('scroll', listenForScroll);
        });

        setTimeout(() => {
            if (tocInnerWrapper && tocInnerWrapper.scrollHeight > tocInnerWrapper.clientHeight) {
                tocInnerWrapper.classList.add('guideline-article__toc-inner-wrapper--overflowing');
            }
        }, 1);
    }
};

const listenForScroll = () => {
    const anchors: NodeListOf<HTMLAnchorElement> = document.querySelectorAll('.guideline-article__toc-anchor');
    const breakpoint: number = window.innerHeight / 2;

    for (let i = 0; i < anchors.length; i++) {
        const anchor = anchors[i];

        const targetHref = anchor.getAttribute('href');
        const targetId = targetHref ? targetHref.substring(1) : null;

        if (targetId) {
            const target = document.querySelector(`[data-heading="${targetId}"]`);

            if (target) {
                const rect = target.getBoundingClientRect();

                if (rect.top > 115 && rect.top < breakpoint) {
                    if (activeLink) {
                        activeLink.classList.remove('guideline-article__toc-anchor--current');
                    }
                    anchor.classList.add('guideline-article__toc-anchor--current');
                    activeLink = anchor;

                    anchor.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                    });

                    break;
                }
            }
        }
    }
};

const listenForBlur = (event: KeyboardEvent): void => {
    if (toc && !toc.contains(event.target as Node)) {
        closeToc();
    }
};

/**
 * Determines if a pixel (same position as the tocButton) is in view or not
 */
const observePixel = (): void => {
    if (!domPixel || !tocButton) {
        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    tocButton?.classList.remove('guideline-article__toc-button--fixed');
                } else {
                    tocButton?.classList.add('guideline-article__toc-button--fixed');
                }
            });
        },
        {
            root: null,
        },
    );

    observer.observe(domPixel);
};

/* Listeners */
window.addEventListener('DOMContentLoaded', () => {
    if (tocInitialized) return;
    tocInitialized = true;

    if (domTocTargetContainer && domParentContainer) {
        initTableOfContents();
        observePixel();
        toc?.classList.remove('guideline-article__toc--fade-in');
    }
});

window.addEventListener('resize', () => {
    if (tocInnerWrapper && tocInnerWrapper.scrollHeight > tocInnerWrapper.clientHeight) {
        tocInnerWrapper.classList.add('guideline-article__toc-inner-wrapper--overflowing');
    } else if (tocInnerWrapper && tocInnerWrapper.scrollHeight <= tocInnerWrapper.clientHeight) {
        tocInnerWrapper.classList.remove('guideline-article__toc-inner-wrapper--overflowing');
    }
});